import { useEffect, useState, type FormEvent } from "react";
import { Eye, EyeOff, LockKeyhole } from "lucide-react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../lib/api";
import { safeReturnTo } from "../lib/format";

export function LoginPage() {
  const { status, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  useEffect(() => { document.title = "登录 · 托尼的个人工作台"; }, []);
  if (status === "authenticated") return <Navigate to={safeReturnTo(params.get("returnTo"))} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password) { setError("请输入账号和密码。"); return; }
    setBusy(true); setError(null);
    try {
      await login(email.trim(), password);
      navigate(safeReturnTo(params.get("returnTo")), { replace: true });
    } catch (reason) {
      setPassword("");
      if (reason instanceof ApiError && reason.status === 429) setError(`尝试次数过多，请在 ${reason.retryAfterSeconds ?? "稍后"} 秒后重试。`);
      else if (reason instanceof ApiError && reason.status === 0) setError("无法连接服务器，请检查网络后重试。");
      else setError("账号或密码不正确，请重新输入。");
    } finally { setBusy(false); }
  };

  return (
    <main className="grid min-h-screen place-items-center bg-canvas px-5 py-10">
      <section className="w-full max-w-[420px]">
        <div className="mb-8 text-center">
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-primary text-lg font-bold text-[var(--color-on-primary)]" aria-hidden="true">T</span>
          <h1 className="mt-4 text-2xl font-bold sm:text-3xl">托尼的个人工作台</h1>
          <p className="mt-2 text-sm text-muted">登录后，从任何设备继续你的工作与成长记录。</p>
        </div>
        <form onSubmit={submit} className="card space-y-5 p-5 sm:p-7" noValidate>
          <div className="flex items-center gap-2"><LockKeyhole size={18} aria-hidden="true" /><h2 className="font-semibold">登录个人空间</h2></div>
          {error ? <div className="rounded-lg bg-[var(--color-danger-soft)] p-3 text-sm text-danger" role="alert">{error}</div> : null}
          <label className="field">
            <span className="field-label">账号</span>
            <input className="input" type="email" autoComplete="username" inputMode="email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} required />
          </label>
          <label className="field">
            <span className="field-label">密码</span>
            <span className="relative">
              <input className="input pr-12" type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} required />
              <button type="button" className="icon-btn absolute right-0.5 top-0.5" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "隐藏密码" : "显示密码"} aria-pressed={showPassword}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
          </label>
          <button className="btn-primary w-full" type="submit" disabled={busy}>{busy ? "正在登录…" : "登录"}</button>
          <p className="text-center text-xs leading-relaxed text-[var(--color-text-subtle)]">系统不提供公开注册。如需重置账号，请通过受控的服务器初始化流程处理。</p>
        </form>
      </section>
    </main>
  );
}
